import { useEffect, useState, type ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Card } from '@/components/ui/Card';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/lib/storeLinks';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

// Bumpado em 2026-07-05 — os apps nativos foram publicados (App Store +
// Google Play), então iOS/Android mobile trocam de "instalar a PWA" pra
// "baixar da loja" (mapfirst-store.jsx). Quem dispensou o banner antigo
// merece ver o convite novo pelo menos uma vez.
const DISMISS_KEY = 'bow-install-dismissed-at-v3';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

function recentlyDismissed(): boolean {
  const raw = window.localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < DISMISS_TTL_MS;
}

type Platform = 'ios' | 'android-chrome' | 'desktop-chrome' | 'firefox' | 'other';

function detectPlatform(ua: string): Platform {
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/firefox/i.test(ua)) return 'firefox';
  if (/android/i.test(ua) && /chrome|samsung/i.test(ua)) return 'android-chrome';
  if (/chrome|edg|opr/i.test(ua) && !/mobile/i.test(ua)) return 'desktop-chrome';
  return 'other';
}

interface InstallPromptProps {
  /**
   * Esconde o banner sem desmontar o componente — os listeners de
   * `beforeinstallprompt` continuam ativos pra não perder o evento (dispara
   * uma vez só). Usado nas rotas de hero full-bleed/no-scroll, onde um card
   * inline no topo do main quebra o layout de viewport fixa.
   */
  hidden?: boolean;
}

export function InstallPrompt({ hidden = false }: InstallPromptProps) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState<Platform>('other');
  const [dismissed, setDismissed] = useState(() => recentlyDismissed());

  useEffect(() => {
    const nav = window.navigator as SafariNavigator;
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      nav.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    setPlatform(detectPlatform(nav.userAgent));

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  function dismiss() {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }

  // No app nativo (Capacitor) o app JÁ está instalado — o webview não reporta
  // display-mode standalone e o UA parece "Android Chrome", então sem este
  // guard o banner "Instalar no Android" apareceria dentro do próprio app.
  if (hidden || installed || dismissed || Capacitor.isNativePlatform()) return null;

  // Caminho 0 — iOS/Android no navegador mobile: os apps nativos já estão
  // publicados nas duas lojas, então o convite vira "baixar da loja" em vez
  // de instalar a PWA (mapfirst-store.jsx). Prioriza sobre `deferred`
  // (beforeinstallprompt) — o app nativo é a experiência melhor agora.
  // Desktop (sem loja mobile pra mandar) mantém o fluxo de PWA abaixo.
  if (platform === 'ios' || platform === 'android-chrome') {
    const isApple = platform === 'ios';
    return (
      <Card className="mb-4 flex items-center gap-3 border border-bow-blue/20 bg-bow-blue/5">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white">
          {isApple ? <AppleGlyph /> : <PlayGlyph />}
        </span>
        <div className="min-w-0 flex-1">
          <span className="block font-black text-bow-royal">Baixe o app da Bow</span>
          <span className="block text-xs text-bow-royal/70">
            Mais rápido que pelo navegador.
          </span>
        </div>
        <a
          href={isApple ? APP_STORE_URL : PLAY_STORE_URL}
          target="_blank"
          rel="noreferrer"
          className="h-9 shrink-0 rounded-lg bg-bow-blue px-4 text-sm font-black leading-9 text-white"
        >
          Baixar
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          className="text-bow-royal/40 hover:text-bow-royal/70"
        >
          ×
        </button>
      </Card>
    );
  }

  // Caminho 1 — Chrome/Edge/Android disparou `beforeinstallprompt`. Botão
  // de instalar com 1 clique (prompt nativo).
  if (deferred) {
    return (
      <Card className="mb-4 flex items-center gap-3 border border-bow-blue/20 bg-bow-blue/5">
        <span className="text-2xl" aria-hidden>
          📲
        </span>
        <div className="min-w-0 flex-1">
          <span className="block font-black text-bow-royal">Instalar app</span>
          <span className="block text-xs text-bow-royal/70">
            Acesse mais rápido direto da tela inicial.
          </span>
        </div>
        <button
          type="button"
          onClick={async () => {
            await deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice.outcome === 'dismissed') dismiss();
            else setDeferred(null);
          }}
          className="h-9 shrink-0 rounded-lg bg-bow-blue px-4 text-sm font-black text-white"
        >
          Instalar
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          className="text-bow-royal/40 hover:text-bow-royal/70"
        >
          ×
        </button>
      </Card>
    );
  }

  // Caminho 2 — fallback manual com instruções por plataforma. Aparece
  // quando o evento nativo não dispara (iOS sempre, Chrome enquanto a
  // heurística de engajamento não bate, browsers sem suporte). Some quando
  // a página entra em standalone (foi instalado).
  const instruction = manualInstructionFor(platform);
  if (!instruction) return null;

  return (
    <Card className="mb-4 flex flex-col gap-2 border border-bow-blue/20 bg-bow-blue/5">
      <div className="flex items-center justify-between">
        <span className="font-black text-bow-royal">{instruction.title}</span>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          className="text-bow-royal/40 hover:text-bow-royal/70"
        >
          ×
        </button>
      </div>
      <p className="text-xs text-bow-royal/70">{instruction.body}</p>
    </Card>
  );
}

// iOS/android-chrome não passam mais por aqui — resolvem no Caminho 0 (banner
// de loja) antes de chegar neste fallback. Só sobra desktop/outros, onde não
// existe app de loja mobile pra oferecer.
function manualInstructionFor(
  platform: Platform
): { title: string; body: ReactNode } | null {
  switch (platform) {
    case 'ios':
    case 'android-chrome':
      return null;
    case 'desktop-chrome':
      return {
        title: 'Instalar como app',
        body: (
          <>
            Clique no ícone de <strong>instalação</strong> (⊕) na barra de
            endereço ou no <strong>menu ⋮</strong> &gt;{' '}
            <strong>"Instalar Bow"</strong>.
          </>
        )
      };
    case 'firefox':
      // Firefox desktop não suporta install. Mobile Firefox: "Instalar"
      // está no menu três pontos.
      return {
        title: 'Adicionar à tela inicial',
        body: (
          <>
            No Firefox móvel, toque em <strong>⋮</strong> e{' '}
            <strong>"Instalar"</strong>. No desktop, considere usar Chrome ou
            Edge pra instalar como app.
          </>
        )
      };
    case 'other':
      return null;
  }
}

function AppleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#001B74" aria-hidden>
      <path d="M17.05 12.55c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.4-2.44-1.59-2.97-1.61-1.26-.13-2.47.74-3.11.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.03-1.43 2.49-.37 6.17 1.03 8.19.68.99 1.5 2.1 2.56 2.06 1.03-.04 1.42-.66 2.66-.66 1.24 0 1.59.66 2.68.64 1.11-.02 1.81-1.01 2.49-2 .78-1.15 1.11-2.26 1.13-2.32-.03-.01-2.17-.83-2.19-3.29z" />
      <path d="M15.1 6.6c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.39 1.23-.52.61-.98 1.59-.86 2.52.91.07 1.84-.46 2.4-1.15z" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path d="M4 3.5 14.5 12 4 20.5c-.3.2-.6-.02-.6-.4V3.9c0-.38.3-.6.6-.4z" fill="#00C072" />
      <path
        d="M4 3.5c.06-.04.14-.05.23 0L18 11.4c.4.23.4.97 0 1.2L4.23 20.5c-.09.05-.17.04-.23 0L14.5 12 4 3.5z"
        fill="#00BBDE"
      />
      <path d="m14.5 12 3.5-2.02c.4-.23.4-.97 0-1.2L14.5 12z" fill="#FFB92A" />
      <path d="m14.5 12 3.5 2.02c.4.23.4.97 0 .78L14.5 12z" fill="#EE263E" />
    </svg>
  );
}
